/**
 * Per-tab persistence for "where I was" in the CRM: the active list page, filters,
 * selected tab, and scroll offset. Stored in sessionStorage so a place is kept while
 * the user navigates in and out of detail views, and forgotten when the tab closes.
 */

type ContactType = "Coach" | "ProspectProfile" | "Parent";

const STORAGE_PREFIX = "crm:";

const key = (...parts: string[]): string => STORAGE_PREFIX + parts.join(":");

export const contactsFilterKey = (contactType: ContactType): string =>
  key("contacts", contactType, "filters");
export const contactsScrollKey = (contactType: ContactType): string =>
  key("contacts", contactType, "scroll");

export const teamsFilterKey = (): string => key("teams", "filters");
export const teamsScrollKey = (): string => key("teams", "scroll");

export const communicationsViewKey = (): string => key("communications", "view");
export const communicationsScrollKey = (): string => key("communications", "scroll");

export const followersViewKey = (): string => key("followers", "view");
export const followersScrollKey = (): string => key("followers", "scroll");

export const unmatchedQueueViewKey = (): string => key("messages", "unmatched", "view");
export const unmatchedQueueScrollKey = (): string => key("messages", "unmatched", "scroll");

export const pendingSubmissionsViewKey = (): string => key("submissions", "pending", "view");
export const pendingSubmissionsScrollKey = (): string => key("submissions", "pending", "scroll");

/**
 * Lists that grow with a "Load more" button restore the pages the user had already
 * pulled in. Capped so returning to a deeply scrolled queue doesn't fire an unbounded
 * burst of requests.
 */
export const MAX_RESTORED_PAGES = 5;

export const saveCrmViewState = (key: string, params: URLSearchParams): void => {
  try {
    const serialized = params.toString();
    if (!serialized) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, serialized);
  } catch (error) {
    console.warn("Failed to save CRM view state to sessionStorage:", error);
  }
};

export const loadCrmViewState = (key: string): URLSearchParams | null => {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return null;
    return new URLSearchParams(stored);
  } catch (error) {
    console.warn("Failed to load CRM view state from sessionStorage:", error);
    return null;
  }
};

/** Reads a 1-based page number, falling back to page 1 for missing or junk values. */
export const readPageParam = (value: string | null): number => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return 1;
  return parsed;
};

export const saveCrmScrollOffset = (key: string, offset: number): void => {
  try {
    if (!Number.isFinite(offset) || offset <= 0) {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, String(Math.round(offset)));
  } catch (error) {
    console.warn("Failed to save CRM scroll offset to sessionStorage:", error);
  }
};

export const loadCrmScrollOffset = (key: string): number => {
  try {
    const stored = sessionStorage.getItem(key);
    if (!stored) return 0;
    const parsed = Number(stored);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch (error) {
    console.warn("Failed to load CRM scroll offset from sessionStorage:", error);
    return 0;
  }
};
